import { useCallback, useState } from "react";
import type { NavigateFunction } from "react-router-dom";

import type { SkillCollectionVersionRecord } from "@/queries/skill-collections";

interface UsePageNavigationParams {
  basePath: string | null;
  isEditing: boolean;
  routeVersionNumber: number | null;
  selectedVersion: SkillCollectionVersionRecord | null;
  latestVersionNumber: number;
  navigate: NavigateFunction;
}

export const usePageNavigation = ({
  basePath,
  isEditing,
  routeVersionNumber,
  selectedVersion,
  latestVersionNumber,
  navigate,
}: UsePageNavigationParams) => {
  const [editorDirty, setEditorDirty] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState<string | null>(null);
  const [editorDiscardKey, setEditorDiscardKey] = useState(0);

  const handleTabChange = useCallback(
    (index: number) => {
      if (!basePath) {
        return;
      }

      if (isEditing && index !== 1 && editorDirty) {
        const versionPath = routeVersionNumber
          ? `${basePath}/version/${routeVersionNumber}`
          : basePath;
        setPendingNavigationPath(versionPath);
        setIsConfirmModalOpen(true);
        return;
      }

      if (index === 1) {
        const versionToEdit = selectedVersion?.number ?? latestVersionNumber;
        navigate(`${basePath}/version/${versionToEdit}/edit`);
        return;
      }

      if (index === 0) {
        if (routeVersionNumber) {
          navigate(`${basePath}/version/${routeVersionNumber}`);
        } else {
          navigate(basePath);
        }
      }
    },
    [
      basePath,
      editorDirty,
      isEditing,
      latestVersionNumber,
      navigate,
      routeVersionNumber,
      selectedVersion?.number,
    ],
  );

  const handleDiscardAndLeave = useCallback(() => {
    const targetPath = pendingNavigationPath;

    setEditorDiscardKey(current => current + 1);
    setEditorDirty(false);
    setPendingNavigationPath(null);
    setIsConfirmModalOpen(false);

    if (targetPath) {
      navigate(targetPath);
    }
  }, [navigate, pendingNavigationPath]);

  const handleEditorSaveSuccess = useCallback(() => {
    setEditorDirty(false);
    setIsConfirmModalOpen(false);
    setPendingNavigationPath(null);
  }, []);

  return {
    editorDirty,
    setEditorDirty,
    isConfirmModalOpen,
    setIsConfirmModalOpen,
    pendingNavigationPath,
    editorDiscardKey,
    handleTabChange,
    handleDiscardAndLeave,
    handleEditorSaveSuccess,
  };
};
